import { users } from '@/api/common';
import { createStore } from 'vuex';
import { redirect } from '@/utils/document';
import { GLOBAL_PREFIX } from '@/router/constants';
import i18n, {
  applyStoredLocaleOverride,
  normalizeLocale,
} from '@/i18n';

const SIDE_NAV_COLLAPSED_SESSION_KEY = 'ui-v2-side-nav-collapsed';
const getInitialSideNavCollapsed = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(SIDE_NAV_COLLAPSED_SESSION_KEY) === 'true';
};

const store = createStore({
  state: {
    app: null,
    breadcrumbs: null,
    showSideNav: false,
    showSideNavCollapse: getInitialSideNavCollapsed(),
    notification: null,
    user: {
      isAuthenticated: false,
    },
  },
  mutations: {
    addNotification(state, payload) {
      state.notification = payload;
    },
    login(state, payload) {
      localStorage.setItem('auth-token', payload.token);
      state.authTokenExists = true;
    },
    resetUser(state) {
      state.user = {
        isAuthenticated: false,
      };
    },
    setApplication(state, payload) {
      state.app = payload;
    },
    setBreadcrumbs(state, payload) {
      state.breadcrumbs = payload;
    },
    setUser(state, payload) {
      state.user = payload;
    },
    setShowSideNav(state, payload) {
      state.showSideNav = payload;
    },
    toggleSideNav(state) {
      // Show or hide the side nav;
      state.showSideNav = !state.showSideNav;
    },
    toggleSideNavCollapse(state) {
      // Expand or collapse the side nav;
      const nextValue = !state.showSideNavCollapse;
      state.showSideNavCollapse = nextValue;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(SIDE_NAV_COLLAPSED_SESSION_KEY, `${nextValue}`);
      }
    },
    setShowSideNavCollapse(state, payload) {
      const collapsed = Boolean(payload);
      state.showSideNavCollapse = collapsed;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(SIDE_NAV_COLLAPSED_SESSION_KEY, `${collapsed}`);
      }
    },
  },
  actions: {
    async refreshUser() {
      const response = await users.fetch();
      if (response.status === 401) {
        // The token is no longer valid, so drop it right away: the logout page
        // checks the user again while a token is stored, and with this one
        // would land back on itself before it can remove it, over and over.
        localStorage.removeItem('auth-token');
        setTimeout(() => {
          redirect(`${GLOBAL_PREFIX}/logout`);
        }, 10)
        return
      }
      const userData = response.data;
      userData.isAuthenticated = true;

      const profileLanguage = normalizeLocale(userData?.language);

      if (!applyStoredLocaleOverride()) {
        i18n.global.locale.value = profileLanguage;
      }
      store.commit('setUser', userData);
      return userData;
    },
  },
  getters: {
    notification(state) {
      return state.notification;
    },
    breadcrumbs(state) {
      return state.breadcrumbs;
    },
    user(state) {
      return state.user;
    },
    app(state) {
      return state.app;
    },
  },
});

export default store;
